import { useState, useCallback, useMemo } from 'react';
import {
  useDeleteOrganizationMutation,
  Organization,
} from '../api';
import { useEntityActions, BaseEntityAction } from '@hooks/useEntityActions';
import { useModal } from '@/hooks/useModal';

// Extend the base actions with organization-specific actions
export type OrganizationAction = BaseEntityAction | 'edit_configuration';

// Define getEntityId outside to ensure stability
const getEntityId = (organization: Organization) => organization.id;

export const useOrganizationActions = () => {
  const [selectedOrganizationForConfiguration, setSelectedOrganizationForConfiguration] = useState<Organization | null>(null);
  
  // Modal state for configuration
  const editConfigurationModal = useModal();
  
  // Base actions using the generic hook
  const baseActions = useEntityActions<Organization, BaseEntityAction>({
    entityName: 'Organization',
    displayProperty: 'name',
    deleteMutation: useDeleteOrganizationMutation,
    getEntityId,
  });

  // Additional action handler for configuration
  const handleEditConfiguration = useCallback((organization: Organization) => {
    setSelectedOrganizationForConfiguration(organization);
    editConfigurationModal.open();
  }, [editConfigurationModal]);

  // Enhanced action handler that includes additional actions
  const handleAction = useCallback((action: OrganizationAction, organization: Organization) => {
    if (action === 'edit_configuration') {
      handleEditConfiguration(organization);
    } else {
      // Delegate all base actions to baseActions
      baseActions.handleAction(action as BaseEntityAction, organization);
    }
  }, [baseActions.handleAction, handleEditConfiguration]);

  // Modal props - no need for intermediate useMemo as they are part of final return object
  const editConfigurationModalProps = {
    isOpen: editConfigurationModal.isOpen,
    onClose: () => {
      editConfigurationModal.close();
      setSelectedOrganizationForConfiguration(null);
    },
  };

  // Memoize the return object
  return useMemo(() => ({
    // Base actions (view, edit, create, delete)
    ...baseActions,
    
    // Override the action handler to include additional actions
    handleAction,
    
    // Additional action state
    selectedOrganizationForConfiguration,
    
    // Additional modal props
    editConfigurationModalProps,
  }), [
    baseActions,
    handleAction,
    selectedOrganizationForConfiguration,
    // De-structure dependencies for modal props to ensure stability
    editConfigurationModal.isOpen,
    editConfigurationModal.close
  ]);
}; 